import React, { FC, useEffect, useState, useRef, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Switch,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  TextField,
  Paper,
  Divider
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import HelpVideoModal from '../system/HelpVideoModal'
import SaveIcon from '@mui/icons-material/Save'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ConfigField from './ConfigField'
import { normalizeConfigForSave } from './normalizeConfigForSave'
import ConfigSourceInfo from './ConfigSourceInfo'
import {
  ConfigEditorProps,
  ConfigEditorHandle,
  ConfigData,
  ConfigReference,
  ConfigEntity,
  ConfigFieldName,
  ConfigFieldSourceInfo,
  ConfigType,
  DEFAULT_CONFIG_DATA,
} from '../../types/config'
import {
  ConfigFieldDefinition,
  ConfigTabDefinition,
  ConfigType as SchemaConfigType,
} from '../../types/gotypes'
import useAccount from '../../hooks/useAccount'
import { getConfigTypeDisplayName } from '../../services/configApi'
import { useConfigSchema } from '../../contexts/configSchema'

const createEmptyConfigData = (): ConfigData => {
  return Object.assign({}, DEFAULT_CONFIG_DATA)
}

const normalizePath = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const trimmed = value.trim()
  if (trimmed === '/') {
    return ''
  }
  return trimmed.replace(/^\/+/, '').replace(/\/$/, '')
}

const referenceMatchesEntity = (reference: ConfigReference, entity: ConfigEntity): boolean => {
  const source = reference.source_entity
  if (!source) {
    return false
  }

  if ((source.config_type ?? ConfigType.Global) !== entity.type) {
    return false
  }

  const sourceCustomer = source.customer ?? ''
  const entityCustomer = entity.customer ?? ''
  if (sourceCustomer !== entityCustomer) {
    return false
  }

  const sourceJob = source.job ?? ''
  const entityJob = entity.job ?? ''
  if (sourceJob !== entityJob) {
    return false
  }

  const sourceFolder = normalizePath(source.folder_path)
  const entityFolder = normalizePath(entity.folder_path)
  if (sourceFolder !== entityFolder) {
    return false
  }

  const sourceTable = source.table_name ?? ''
  const entityTable = entity.table_name ?? ''
  if (sourceTable !== entityTable) {
    return false
  }

  return true
}

const ConfigEditor = forwardRef<ConfigEditorHandle, ConfigEditorProps>(({
  open,
  onClose,
  entity,
  mergedConfig,
  onSave,
  onSaved,
  onEditEntity,
  handleNodeSettings,
  loading = false,
  saving = false,
  variant = 'modal',
  hideActions = false,
  onChangeDetected,
  readOnly = false,
  filterTabs,
  alwaysEnableSave = false,
  hideHeader = false,
  showSectionSave = false,
  autoSave = false
}, ref) => {

  const account = useAccount()
  const { schema, loadSchema } = useConfigSchema()
  const [formData, setFormData] = useState<ConfigData>(() => createEmptyConfigData())
  const [initialFormData, setInitialFormData] = useState<ConfigData>(() => createEmptyConfigData())
  const [selectedTab, setSelectedTab] = useState(0)
  const [helpVideoUrl, setHelpVideoUrl] = useState<string | null>(null)
  const [sourceInfoState, setSourceInfoState] = useState<{
    reference: ConfigReference | null
    anchorEl: HTMLElement | null
    open: boolean
  }>({
    reference: null,
    anchorEl: null,
    open: false
  })
  // Auto-save refs
  const formDataRef = useRef(formData)
  formDataRef.current = formData
  const initialFormDataRef = useRef(initialFormData)
  initialFormDataRef.current = initialFormData
  const saveInProgressRef = useRef(false)
  const saveQueuedRef = useRef(false)

  // Entity key for detecting actual entity changes (not just reference changes from refresh)
  const entityKey = entity
    ? [entity.type ?? '', entity.customer ?? '', entity.job ?? '', entity.folder_path ?? '', entity.table_name ?? ''].join('|')
    : ''
  const entityKeyRef = useRef('')

  const shouldShowAdvanced = false
  // account.user?.role_permissions?.isManager ? true : false

  // Load schema on mount
  useEffect(() => {
    loadSchema()
  }, [loadSchema])

  // Helper to convert frontend ConfigType to schema ConfigType (number)
  const entityTypeAsNumber = entity?.type as unknown as number

  // Get field definition from schema
  const getSchemaFieldDef = (fieldName: string): ConfigFieldDefinition | undefined => {
    return schema?.fields?.[fieldName]
  }

  // Get field label from schema (with fallback)
  const getFieldLabel = (fieldName: string): string => {
    const def = getSchemaFieldDef(fieldName)
    return def?.label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  // Get field description from schema
  const getFieldDescription = (fieldName: string): string | undefined => {
    return getSchemaFieldDef(fieldName)?.description
  }

  // Get field placeholder from schema (with config-type-specific override)
  const getFieldPlaceholder = (fieldName: string): string | undefined => {
    const def = getSchemaFieldDef(fieldName)
    if (!def) return undefined

    // Check for config-type-specific placeholder
    if (def.placeholder_by_config_type && entity?.type !== undefined) {
      const typeSpecific = def.placeholder_by_config_type[entityTypeAsNumber]
      if (typeSpecific) return typeSpecific
    }

    return def.placeholder
  }

  // Check if field is visible for current entity type
  const isFieldVisible = (fieldName: string): boolean => {
    const def = getSchemaFieldDef(fieldName)
    if (!def || !entity) return false

    // Check if entity type is in visible_at list
    if (!def.visible_at?.includes(entityTypeAsNumber)) return false

    // Check if field requires root permission
    if (def.requires_root && !account.user?.domain_permissions?.isRoot) return false

    return true
  }

  // Auto-save: serialized queue that always reads latest formDataRef.current
  const triggerAutoSave = useCallback(async () => {
    if (!autoSave || !entity) return

    // If a save is already in progress, queue another save for when it finishes
    if (saveInProgressRef.current) {
      saveQueuedRef.current = true
      return
    }

    // No-change guard: skip if nothing has actually changed
    const currentData = formDataRef.current
    const initialData = initialFormDataRef.current
    const fieldNames = Object.keys(currentData) as ConfigFieldName[]
    const anyChanged = fieldNames.some(fn => {
      const cur = currentData[fn]
      const init = initialData[fn]
      if (Array.isArray(cur) || Array.isArray(init)) {
        return JSON.stringify(cur || []) !== JSON.stringify(init || [])
      }
      return String(cur ?? '') !== String(init ?? '')
    })
    if (!anyChanged) return

    saveInProgressRef.current = true
    try {
      await onSave(normalizeConfigForSave(currentData, schema))
      if (onSaved) await onSaved()
      // Update initial to match what was saved so next blur detects new changes
      const savedSnapshot = { ...currentData }
      setInitialFormData(savedSnapshot)
      initialFormDataRef.current = savedSnapshot
    } catch (error) {
      console.error('Auto-save failed:', error)
    } finally {
      saveInProgressRef.current = false
      // If another save was queued while this one was in progress, run it now
      if (saveQueuedRef.current) {
        saveQueuedRef.current = false
        triggerAutoSave()
      }
    }
  }, [autoSave, entity, onSave, onSaved, schema])

  // Get visible tabs for current entity
  const visibleTabs = useMemo(() => {
    if (!schema || !entity) return []

    return schema.tabs
      .filter(tab => {
        // Filter by allowed tabs if specified
        if (filterTabs && filterTabs.length > 0 && !filterTabs.includes(tab.id)) {
          return false
        }
        // Check if any field in this tab is visible for the current entity type
        const hasVisibleFields = Object.values(schema.fields).some(
          field => field.tab === tab.id && field.visible_at?.includes(entityTypeAsNumber)
        )
        // Check advanced permission
        const passesAdvanced = !tab.advanced || shouldShowAdvanced
        return hasVisibleFields && passesAdvanced
      })
      .sort((a, b) => a.order - b.order)
  }, [schema, entity, entityTypeAsNumber, shouldShowAdvanced, filterTabs])

  // Get fields for a specific tab, sorted by order
  const getFieldsForTab = (tabId: string): ConfigFieldDefinition[] => {
    if (!schema || !entity) return []

    return Object.values(schema.fields)
      .filter(field => {
        if (field.tab !== tabId) return false
        if (!field.visible_at?.includes(entityTypeAsNumber)) return false
        if (field.requires_root && !account.user?.domain_permissions?.isRoot) return false
        return true
      })
      .sort((a, b) => a.order - b.order)
  }

  // Get current tab definition
  const getCurrentTab = (): ConfigTabDefinition | undefined => {
    return visibleTabs[selectedTab]
  }

  // Get field names from schema or fallback to formData keys
  const getFieldNames = (): ConfigFieldName[] => {
    if (schema?.fields) {
      return Object.keys(schema.fields) as ConfigFieldName[]
    }
    return Object.keys(formData) as ConfigFieldName[]
  }

  // Check if a field is an array type
  const isArrayField = (fieldName: string): boolean => {
    const def = getSchemaFieldDef(fieldName)
    return def?.type === 'jsonarray'
  }

  // Helper to check if a field has changed
  const fieldHasChanged = (fieldName: ConfigFieldName): boolean => {
    const currentValue = formData[fieldName]
    const initialValue = initialFormData[fieldName]

    // Handle jsonarray fields - normalize to JSON string for comparison
    if (isArrayField(fieldName)) {
      const normalizeArrayValue = (val: unknown): string => {
        if (Array.isArray(val)) return JSON.stringify(val)
        if (typeof val === 'string') {
          try {
            const parsed = JSON.parse(val)
            return JSON.stringify(parsed)
          } catch {
            return val
          }
        }
        return '[]'
      }
      return normalizeArrayValue(currentValue) !== normalizeArrayValue(initialValue)
    }

    // Handle other array fields (like custom_personas)
    if (Array.isArray(currentValue) || Array.isArray(initialValue)) {
      return JSON.stringify(currentValue || []) !== JSON.stringify(initialValue || [])
    }

    // Handle non-string types with JSON comparison
    if (typeof currentValue !== 'string' && typeof initialValue !== 'string') {
      return JSON.stringify(currentValue ?? null) !== JSON.stringify(initialValue ?? null)
    }

    // Handle string fields
    const currentStr = String(currentValue ?? '')
    const initialStr = String(initialValue ?? '')
    return currentStr.trim() !== initialStr.trim()
  }

  // Expose imperative methods via ref
  useImperativeHandle(ref, () => ({
    // With autoSave on, an explicit Save joins the same serialized queue as a
    // blur, so the two can never write over each other.
    save: autoSave ? triggerAutoSave : handleSave,
    cancel: handleCancel,
    hasChanges: () => {
      const fieldNames = getFieldNames()
      return fieldNames.some(fieldHasChanged)
    }
  }), [formData, initialFormData, schema, autoSave, triggerAutoSave])

  // Notify parent of changes
  useEffect(() => {
    if (onChangeDetected) {
      const fieldNames = getFieldNames()
      const changed = fieldNames.some(fieldHasChanged)
      onChangeDetected(changed)
    }
  }, [formData, initialFormData, onChangeDetected, schema])

  useEffect(() => {
    if (!open || !entity || !mergedConfig) {
      return
    }

    // When autoSave is true, skip the reset if the entity hasn't actually changed.
    // This prevents the openConfigEntity refresh (called by useConfig after every save)
    // from wiping in-progress edits.
    if (autoSave && entityKeyRef.current === entityKey) {
      return
    }
    entityKeyRef.current = entityKey

    const nextFormData = createEmptyConfigData()
    const fieldNames = getFieldNames()

    fieldNames.forEach(fieldName => {
      const fieldDetail = mergedConfig.fields?.[fieldName]
      if (!fieldDetail) {
        return
      }

      const fieldDefinition = getSchemaFieldDef(fieldName)
      const usesOverrideStrategy = fieldDefinition?.strategy === 'override'

      // First, try to find a source that matches the current entity (local override)
      const entitySource = fieldDetail.sources?.find(source => referenceMatchesEntity(source, entity))
      if (entitySource && typeof entitySource.value === 'string') {
        // For JSONArray fields, parse the JSON string
        if (fieldDefinition?.type === 'jsonarray') {
          try {
            nextFormData[fieldName] = JSON.parse(entitySource.value) as any
          } catch (err) {
            console.error(`Failed to parse JSON for field ${fieldName}:`, err)
            nextFormData[fieldName] = [] as any
          }
        } else if (fieldDefinition?.type === 'bool') {
          // Convert string "true"/"false" to boolean
          nextFormData[fieldName] = (entitySource.value === 'true') as any
        } else {
          nextFormData[fieldName] = entitySource.value as any
        }
      } else if (entitySource && fieldDefinition?.type === 'bool') {
        // Handle case where entitySource.value is already a boolean
        nextFormData[fieldName] = (entitySource.value === true || entitySource.value === 'true') as any
      } else if (usesOverrideStrategy && mergedConfig.data && fieldName in mergedConfig.data) {
        // For override fields, show the effective merged value as the starting point
        nextFormData[fieldName] = mergedConfig.data[fieldName] as any
      }
    })

    setFormData(nextFormData)
    setInitialFormData({ ...nextFormData })
    setSelectedTab(0)
  }, [open, entity, mergedConfig, schema])

  const handleFieldChange = (fieldName: ConfigFieldName) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  const handleShowSourceInfo = (reference: ConfigReference, event?: React.MouseEvent<HTMLButtonElement>) => {
    setSourceInfoState({
      reference,
      anchorEl: event?.currentTarget || null,
      open: true
    })
  }

  const handleSourceInfoClose = () => {
    setSourceInfoState({
      reference: null,
      anchorEl: null,
      open: false
    })
  }

  const handleEditSource = async (sourceEntity: ConfigEntity) => {
    if (!onEditEntity) {
      handleSourceInfoClose()
      return
    }

    try {
      await onEditEntity(sourceEntity)
    } catch (error) {
      console.error('Failed to edit source entity:', error)
    } finally {
      handleSourceInfoClose()
    }
  }

  const handleSave = async () => {
    if (!entity) {
      return
    }

    const shouldClose = variant === 'modal'

    const normalizedData = normalizeConfigForSave(formData, schema)

    try {
      await onSave(normalizedData)
      if (onSaved) {
        await onSaved()
      }
      setInitialFormData({ ...formData })
      if (shouldClose) {
        onClose()
      }
    } catch (error) {
      console.error('Error saving config:', error)
    }
  }

  const handleCancel = () => {
    const shouldClose = variant === 'modal'

    if (shouldClose) {
      const blank = createEmptyConfigData()
      setFormData(blank)
      setInitialFormData({ ...blank })
      setSelectedTab(0)
      onClose()
      return
    }

    setFormData({ ...initialFormData })
    setSelectedTab(0)
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
  }

  const getFieldSources = (fieldName: ConfigFieldName): ConfigFieldSourceInfo[] => {
    if (!mergedConfig) {
      return []
    }

    const detail = mergedConfig.fields?.[fieldName]
    const references = detail?.sources ?? []

    return references.map(reference => ({
      reference,
      isLocal: entity ? referenceMatchesEntity(reference, entity) : false
    }))
  }

  const getFieldStrategy = (fieldName: ConfigFieldName) => {
    const definition = getSchemaFieldDef(fieldName)
    const detailStrategy = mergedConfig?.fields?.[fieldName]?.strategy
    return detailStrategy || definition?.strategy || 'merge'
  }

  const hasChanges = useMemo(() => {
    const fieldNames = getFieldNames()
    return fieldNames.some(fieldHasChanged)
  }, [formData, initialFormData, schema])

  // Get tab definition by ID
  const getTabDef = (tabId: string): ConfigTabDefinition | undefined => {
    return schema?.tabs?.find(t => t.id === tabId)
  }

  // Render methods for each section
  const renderTableSection = () => {
    const tableAnalysisPromptFieldName: ConfigFieldName = 'table_analysis_prompt'
    const tableAnalysisPromptSources = getFieldSources(tableAnalysisPromptFieldName)
    const tableAnalysisPromptStrategy = getFieldStrategy(tableAnalysisPromptFieldName)
    const tableAnalysisPromptDef = getSchemaFieldDef(tableAnalysisPromptFieldName)
    const tableTabDef = getTabDef('table')

    return (
      <Box>
        {tableTabDef?.help_video_url && (
          <Button
            size="small"
            color="info"
            startIcon={<InfoIcon />}
            onClick={() => setHelpVideoUrl(tableTabDef.help_video_url!)}
            sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' }, mb: 2 }}
          >
            Help Video
          </Button>
        )}
        {isFieldVisible('table_view_mode') && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="table-view-mode-label">
              {getFieldLabel('table_view_mode')}
            </InputLabel>
            <Select
              labelId="table-view-mode-label"
              id="table-view-mode-select"
              value={formData.table_view_mode || 'table'}
              label={getFieldLabel('table_view_mode')}
              onChange={(e) => {
                handleFieldChange('table_view_mode')(e.target.value)
                if (autoSave) {
                  // Update ref with new value and trigger save
                  const newData = { ...formDataRef.current, table_view_mode: e.target.value as any }
                  formDataRef.current = newData
                  triggerAutoSave()
                }
              }}
              disabled={readOnly}
            >
              {getSchemaFieldDef('table_view_mode')?.enum_options?.map(opt => (
                <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
              )) || (
                <>
                  <MenuItem value="table">Table</MenuItem>
                  <MenuItem value="debug">Debug</MenuItem>
                </>
              )}
            </Select>
          </FormControl>
        )}
        <ConfigField
          fieldName={tableAnalysisPromptFieldName}
          label={getFieldLabel(tableAnalysisPromptFieldName)}
          value={formData.table_analysis_prompt}
          strategy={tableAnalysisPromptStrategy}
          sources={tableAnalysisPromptSources}
          onChange={handleFieldChange(tableAnalysisPromptFieldName)}
          onShowSourceInfo={handleShowSourceInfo}
          multiline
          rows={tableAnalysisPromptDef?.rows || 4}
          placeholder={getFieldPlaceholder(tableAnalysisPromptFieldName)}
          readOnly={readOnly}
          onBlur={autoSave ? triggerAutoSave : undefined}
        />
        {isFieldVisible('include_side_label') && (
          <FormControlLabel
            control={
              <Switch
                checked={formData.include_side_label ?? true}
                onChange={(e) => {
                  handleFieldChange('include_side_label')(e.target.checked as any)
                  if (autoSave) {
                    const newData = { ...formDataRef.current, include_side_label: e.target.checked }
                    formDataRef.current = newData
                    triggerAutoSave()
                  }
                }}
                disabled={readOnly}
              />
            }
            label={getFieldLabel('include_side_label')}
          />
        )}
      </Box>
    )
  }

  const renderThemeAISection = () => {
    if (!entity || (entity.type !== ConfigType.Customer && entity.type !== ConfigType.Job)) {
      return null
    }

    const themeAIExtractPromptFieldName: ConfigFieldName = 'theme_ai_extract_prompt'
    const themeAIExtractPromptSources = getFieldSources(themeAIExtractPromptFieldName)
    const themeAIExtractPromptStrategy = getFieldStrategy(themeAIExtractPromptFieldName)
    const themeAIExtractPromptDef = getSchemaFieldDef(themeAIExtractPromptFieldName)

    const themeAIMatchPromptFieldName: ConfigFieldName = 'theme_ai_match_prompt'
    const themeAIMatchPromptSources = getFieldSources(themeAIMatchPromptFieldName)
    const themeAIMatchPromptStrategy = getFieldStrategy(themeAIMatchPromptFieldName)
    const themeAIMatchPromptDef = getSchemaFieldDef(themeAIMatchPromptFieldName)

    return (
      <Box>
        <ConfigField
          fieldName={themeAIExtractPromptFieldName}
          label={getFieldLabel(themeAIExtractPromptFieldName)}
          value={formData.theme_ai_extract_prompt}
          strategy={themeAIExtractPromptStrategy}
          sources={themeAIExtractPromptSources}
          onChange={handleFieldChange(themeAIExtractPromptFieldName)}
          onShowSourceInfo={handleShowSourceInfo}
          multiline
          rows={themeAIExtractPromptDef?.rows || 4}
          placeholder={getFieldPlaceholder(themeAIExtractPromptFieldName)}
          readOnly={readOnly}
          onBlur={autoSave ? triggerAutoSave : undefined}
        />
        <ConfigField
          fieldName={themeAIMatchPromptFieldName}
          label={getFieldLabel(themeAIMatchPromptFieldName)}
          value={formData.theme_ai_match_prompt}
          strategy={themeAIMatchPromptStrategy}
          sources={themeAIMatchPromptSources}
          onChange={handleFieldChange(themeAIMatchPromptFieldName)}
          onShowSourceInfo={handleShowSourceInfo}
          multiline
          rows={themeAIMatchPromptDef?.rows || 4}
          placeholder={getFieldPlaceholder(themeAIMatchPromptFieldName)}
          readOnly={readOnly}
          onBlur={autoSave ? triggerAutoSave : undefined}
        />
      </Box>
    )
  }

  const renderExecutiveReportsSection = () => {
    if (!entity || (entity.type !== ConfigType.Customer && entity.type !== ConfigType.Job)) {
      return null
    }

    const execReportTableAnalysisPromptFieldName: ConfigFieldName = 'exec_report_table_analysis_prompt'
    const execReportTableAnalysisPromptSources = getFieldSources(execReportTableAnalysisPromptFieldName)
    const execReportTableAnalysisPromptStrategy = getFieldStrategy(execReportTableAnalysisPromptFieldName)
    const execReportTableAnalysisPromptDef = getSchemaFieldDef(execReportTableAnalysisPromptFieldName)

    const execReportSectionSynthesisPromptFieldName: ConfigFieldName = 'exec_report_section_synthesis_prompt'
    const execReportSectionSynthesisPromptSources = getFieldSources(execReportSectionSynthesisPromptFieldName)
    const execReportSectionSynthesisPromptStrategy = getFieldStrategy(execReportSectionSynthesisPromptFieldName)
    const execReportSectionSynthesisPromptDef = getSchemaFieldDef(execReportSectionSynthesisPromptFieldName)

    return (
      <Box>
        {isFieldVisible(execReportTableAnalysisPromptFieldName) && (
          <ConfigField
            fieldName={execReportTableAnalysisPromptFieldName}
            label={getFieldLabel(execReportTableAnalysisPromptFieldName)}
            value={formData.exec_report_table_analysis_prompt}
            strategy={execReportTableAnalysisPromptStrategy}
            sources={execReportTableAnalysisPromptSources}
            onChange={handleFieldChange(execReportTableAnalysisPromptFieldName)}
            onShowSourceInfo={handleShowSourceInfo}
            multiline
            rows={execReportTableAnalysisPromptDef?.rows || 4}
            placeholder={getFieldPlaceholder(execReportTableAnalysisPromptFieldName)}
            readOnly={readOnly}
            onBlur={autoSave ? triggerAutoSave : undefined}
          />
        )}
        {isFieldVisible(execReportSectionSynthesisPromptFieldName) && (
          <ConfigField
            fieldName={execReportSectionSynthesisPromptFieldName}
            label={getFieldLabel(execReportSectionSynthesisPromptFieldName)}
            value={formData.exec_report_section_synthesis_prompt}
            strategy={execReportSectionSynthesisPromptStrategy}
            sources={execReportSectionSynthesisPromptSources}
            onChange={handleFieldChange(execReportSectionSynthesisPromptFieldName)}
            onShowSourceInfo={handleShowSourceInfo}
            multiline
            rows={execReportSectionSynthesisPromptDef?.rows || 4}
            placeholder={getFieldPlaceholder(execReportSectionSynthesisPromptFieldName)}
            readOnly={readOnly}
            onBlur={autoSave ? triggerAutoSave : undefined}
          />
        )}
      </Box>
    )
  }

  const statsReviewFieldNames: ConfigFieldName[] = ['stats_reviewer_name', 'stats_reviewer_team', 'stats_reviewer_contact', 'stats_reviewer_email']

  const renderStatsReviewSection = () => {
    if (!entity || (entity.type !== ConfigType.Customer && entity.type !== ConfigType.Job)) {
      return null
    }

    const fieldNames = statsReviewFieldNames.filter(isFieldVisible)
    if (fieldNames.length === 0) return null

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {'Who checks advanced analyses (regression, key driver, TURF, segmentation, factor, correspondence, conjoint) before they are shared. Their name appears on the notice the agent shows.'}
        </Typography>
        {fieldNames.map(fieldName => (
          <ConfigField
            key={fieldName}
            fieldName={fieldName}
            label={getFieldLabel(fieldName)}
            value={formData[fieldName] as string | undefined}
            strategy={getFieldStrategy(fieldName)}
            sources={getFieldSources(fieldName)}
            onChange={handleFieldChange(fieldName)}
            onShowSourceInfo={handleShowSourceInfo}
            multiline={false}
            rows={1}
            placeholder={getFieldPlaceholder(fieldName)}
            readOnly={readOnly}
            onBlur={autoSave ? triggerAutoSave : undefined}
          />
        ))}
      </Box>
    )
  }

  const renderAutoCoderSection = () => {
    if (!entity || entity.type !== ConfigType.Job) {
      return null
    }

    const autocoderTabDef = getTabDef('autocoder')
    const carbonLabelsFieldName: ConfigFieldName = 'autocoder_carbon_labels'
    const carbonLabelsFieldDef = getSchemaFieldDef(carbonLabelsFieldName)
    const carbonLabelsSources = getFieldSources(carbonLabelsFieldName)
    const carbonLabelsStrategy = getFieldStrategy(carbonLabelsFieldName)

    // Convert array to JSON string for display/editing
    const currentValue = formData.autocoder_carbon_labels
    const displayValue = Array.isArray(currentValue)
      ? JSON.stringify(currentValue)
      : typeof currentValue === 'string'
        ? currentValue  // Keep string as-is for editing
        : '[]'

    const handleJsonArrayChange = (value: string) => {
      try {
        const parsed = JSON.parse(value)
        if (Array.isArray(parsed)) {
          setFormData(prev => ({ ...prev, autocoder_carbon_labels: parsed }))
          return
        }
      } catch {
        // Invalid JSON - continue to store raw value
      }
      // Store raw string for any non-array case (allows user to continue editing)
      setFormData(prev => ({ ...prev, autocoder_carbon_labels: value as any }))
    }

    return (
      <Box>
        {autocoderTabDef?.help_video_url && (
          <Button
            size="small"
            color="info"
            startIcon={<InfoIcon />}
            onClick={() => setHelpVideoUrl(autocoderTabDef.help_video_url!)}
            sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' }, mb: 2 }}
          >
            Help Video
          </Button>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {carbonLabelsFieldDef?.description || 'Configure which labels should become Carbon variables for cross-tabulation.'}
        </Typography>
        <ConfigField
          fieldName={carbonLabelsFieldName}
          label={getFieldLabel(carbonLabelsFieldName)}
          value={displayValue}
          strategy={carbonLabelsStrategy}
          sources={carbonLabelsSources}
          onChange={handleJsonArrayChange}
          onShowSourceInfo={handleShowSourceInfo}
          multiline={false}
          rows={1}
          placeholder={getFieldPlaceholder(carbonLabelsFieldName)}
          readOnly={readOnly}
          onBlur={autoSave ? triggerAutoSave : undefined}
        />
      </Box>
    )
  }

  const renderSectionHeader = (title: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {showSectionSave && !autoSave && !readOnly && (
        <Button
          size="small"
          variant="contained"
          color="info"
          startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !hasChanges}
          sx={{ mb: 1 }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      )}
    </Box>
  )

  const renderActionButtons = () => (
    <>
      <Button
        onClick={handleCancel}
        disabled={saving}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
        disabled={saving || (!alwaysEnableSave && !hasChanges) || loading}
      >
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </>
  )

  if (!entity) {
    return null
  }

  // Modal variant - Dialog with tabs
  if (variant === 'modal') {
    return (
      <>
        <Dialog
          open={open}
          onClose={handleCancel}
          maxWidth="md"
          fullWidth
          aria-labelledby="config-editor-title"
          aria-describedby="config-editor-description"
        >
          <DialogTitle id="config-editor-title">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" component="span">
                Configure {entity.display_name}
              </Typography>
              <Chip
                label={getConfigTypeDisplayName(entity.type)}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </DialogTitle>

          <DialogContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filterTabs && filterTabs.length > 0 ? (
              // Filtered tabs mode - render only requested sections without tab UI
              <>
                {filterTabs.includes('table') && renderTableSection()}
                {filterTabs.includes('theme_ai') && shouldShowAdvanced && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && renderThemeAISection()}
                {filterTabs.includes('exec_reports') && shouldShowAdvanced && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && renderExecutiveReportsSection()}
                {filterTabs.includes('autocoder') && entity.type === ConfigType.Job && renderAutoCoderSection()}
                {filterTabs.includes('stats_review') && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && renderStatsReviewSection()}
              </>
            ) : (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={selectedTab} onChange={handleTabChange}>
                    <Tab label="Table" />
                    {
                      shouldShowAdvanced && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && (
                        <Tab label="Theme AI" />
                      )
                    }
                    {
                      shouldShowAdvanced && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && (
                        <Tab label="Executive Reports" />
                      )
                    }
                  </Tabs>
                </Box>

                {selectedTab === 0 && renderTableSection()}
                {selectedTab === 1 && shouldShowAdvanced && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && renderThemeAISection()}
                {selectedTab === 2 && shouldShowAdvanced && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && renderExecutiveReportsSection()}

              </>
            )}
          </DialogContent>

          {!hideActions && (
            <DialogActions>
              {renderActionButtons()}
            </DialogActions>
          )}
        </Dialog>

      <ConfigSourceInfo
        reference={sourceInfoState.reference}
        anchorEl={sourceInfoState.anchorEl}
        open={sourceInfoState.open}
        onClose={handleSourceInfoClose}
        onEditSource={handleEditSource}
      />

      </>
    )
  }

  // Form variant - Page-embedded without tabs
  return (
    <>
      {!hideHeader && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" component="h2">
              Configure {entity.display_name}
            </Typography>
            <Chip
              label={getConfigTypeDisplayName(entity.type)}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Paper>
      )}

        {loading && !autoSave ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Table Section */}
            {(!filterTabs || filterTabs.includes('table')) && (
              <Paper sx={{ p: 2, mb: 0 }}>
                {entity.type === ConfigType.Customer && renderSectionHeader('About Your Organisation')}
                {entity.type === ConfigType.Job && renderSectionHeader('Your Job')}
                <Divider sx={{ mb: 2 }} />
                {renderTableSection()}
              </Paper>
            )}

            {/* Theme AI Section */}
            {shouldShowAdvanced && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && (!filterTabs || filterTabs.includes('theme_ai')) && (
              <Paper sx={{ p: 2, mb: 0 }}>
                {renderSectionHeader('Theme AI')}
                <Divider sx={{ mb: 2 }} />
                {renderThemeAISection()}
              </Paper>
            )}

            {/* Executive Reports Section */}
            {shouldShowAdvanced && (entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && (!filterTabs || filterTabs.includes('exec_reports')) && (
              <Paper sx={{ p: 2, mb: 0 }}>
                {renderSectionHeader('Executive Reports')}
                <Divider sx={{ mb: 2 }} />
                {renderExecutiveReportsSection()}
              </Paper>
            )}

            {/* AutoCoder Section */}
            {entity.type === ConfigType.Job && (!filterTabs || filterTabs.includes('autocoder')) && (
              <Paper sx={{ p: 2, mb: 0 }}>
                {renderSectionHeader('AutoCoder')}
                <Divider sx={{ mb: 2 }} />
                {renderAutoCoderSection()}
              </Paper>
            )}

            {/* Stats Review Section */}
            {(entity.type === ConfigType.Customer || entity.type === ConfigType.Job) && statsReviewFieldNames.some(isFieldVisible) && (!filterTabs || filterTabs.includes('stats_review')) && (
              <Paper sx={{ p: 2, mb: 0 }}>
                {renderSectionHeader(getTabDef('stats_review')?.label || 'Stats review')}
                <Divider sx={{ mb: 2 }} />
                {renderStatsReviewSection()}
              </Paper>
            )}

            {/* Action Buttons */}
            {!hideActions && !autoSave && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                {renderActionButtons()}
              </Box>
            )}
          </Box>
        )}

      <ConfigSourceInfo
        reference={sourceInfoState.reference}
        anchorEl={sourceInfoState.anchorEl}
        open={sourceInfoState.open}
        onClose={handleSourceInfoClose}
        onEditSource={handleEditSource}
      />

      <HelpVideoModal open={!!helpVideoUrl} onClose={() => setHelpVideoUrl(null)} url={helpVideoUrl || ''} />
    </>
  )
})

ConfigEditor.displayName = 'ConfigEditor'

export default ConfigEditor
